import { TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { analyzedPromptRunCountOf, type FiltersPayload } from '../GovernanceAnalyticsAiVisibility.utils';
import { createPath } from '../GovernanceTaxonomyManagement.utils';
import type { ITaxonomyTermFacetSource } from '../Onboarding/ContentInventory/Components/useCachedTaxonomyData';
import { ADD_AS_SOURCE_HINT, ADD_AS_SOURCE_LABEL, SourceGrouping, UNCURATED_SOURCE_HINT } from './citationSources.utils';
import {
  barWidthPercent,
  buildSourceDrillDownQuery,
  buildSourceRows,
  buildSourceSeries,
  citationCountLabel,
  DEFAULT_SOURCE_SORT,
  formatPercent,
  presenceCountLabel,
  SOURCE_CITATION_SHARE_DEFINITION,
  SOURCE_LOAD_MORE_LABEL,
  SOURCE_PAGE_SIZE,
  SOURCE_PRESENCE_DEFINITION,
  SOURCE_SUPPORTS_DEFINITION,
  SOURCE_TREND_LIMIT,
  SOURCES_EMPTY_STATE,
  SOURCES_SECTION_CAPTION,
  sourceEntityOptions,
  SourceRow,
  SourceSortColumn,
  SourceStatsRow,
  sourceStatsEntities,
  sourceVisibilityLabel,
  sortSourceStats,
  SUPPORT_CHIP_LIMIT,
  totalCitations,
} from './GovernanceAnalyticsSources.utils';
import { SuggestedTermIcon } from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { GovernanceAnalyticsSectionCard } from './GovernanceAnalyticsSectionCard';
import { GovernanceMetricInfo } from './GovernanceMetricInfo';
import { GovernanceSortableHeader, GovernanceSortDirection } from './GovernanceSortableHeader';
import { GovernanceSparkline } from './GovernanceSparkline';
import { GOVERNANCE_COMPARISON_PALETTE, readableTextColor, readPrimaryColor } from './governanceThemeColors';
import { buildEntityColorMap, collectColorableTermKeys } from './entityPalette';
import { useTermColors } from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { SectionLoadFailed, TRENDS_LOAD_FAILED_MESSAGE, useRetry } from './SectionLoadFailed';
import { TableRowsSkeleton } from './BrandPresenceSkeleton';
import { SourceGroupingToggle } from './SourceChips';
import { IFaviconUrlSource, SourceFavicon } from './SourceFavicon';
import { termStatusPresentation } from './termStatusPresentation';
import {
  fetchSourceStatsHistory,
  fetchSourceStatsTotal,
  SourceHistoryGranularity,
  SourceStatsApi,
  SourceStatsQuery,
} from './sourceStats.client';
import { useAuthoritySources } from './useAuthoritySources';
import { useEntityTermIndex } from './useEntityTermIndex';

/**
 * Everything this module asks of the client, as one typed slice: the stats calls, the per-facet term
 * lists behind both taxonomies, and the favicon URL each row draws.
 *
 * Narrow on purpose. The real `ISyncCoreApi` satisfies it at the call site, and a test can build the
 * three seams by hand with no cast — which is what lets the laziness assertions watch real calls
 * rather than a stand-in for them.
 */
export type GovernanceAnalyticsSourcesApi = SourceStatsApi & ITaxonomyTermFacetSource & IFaviconUrlSource;

/** How many buckets the trend column covers. Enough to read a direction, short enough to stay legible. */
const TREND_BUCKETS = 8;

/** Sentinel for the "every entity" choice in the picker — never sent to the server. */
const ALL_ENTITIES = '';

/** The colour a Supports chip falls back to when the entity is neither the customer nor a rival. */
const UNTRACKED_ENTITY_COLOR = '#64748b';

/** Said when the sources could not be loaded at all, as opposed to there being none. */
const SOURCES_REQUEST_FAILED = 'Sources could not be loaded for this window.';

interface GovernanceAnalyticsSourcesProps {
  api?: GovernanceAnalyticsSourcesApi;
  statsFilters: FiltersPayload;
  hasSelectedPurpose: boolean;
  /**
   * Every analyzed prompt run in the window, and whether that number has settled.
   *
   * undefined = the deployment does not report one, which is not the same as none having run.
   */
  analyzedPromptRunCount?: number;
  analyzedPromptRunsLoading: boolean;
  /** How the trends elsewhere on the page are bucketed, so the sparklines cover the same span. */
  granularity: SourceHistoryGranularity;
  grouping: SourceGrouping;
  onGroupingChange: (next: SourceGrouping) => void;
}

/**
 * The Analytics "Sources" module.
 *
 * Ranks the websites AI engines cite by how many answers cite them, and says which brands each one
 * was cited for — the question "which sources drive my brand versus my competitor's" that no other
 * surface answers.
 *
 * Curation never gates a row: a website nobody has added to the taxonomy still appears, muted, with
 * the affordance to add it. Every label comes from the same resolution the answers list uses.
 */
export function GovernanceAnalyticsSources({
  api,
  statsFilters,
  hasSelectedPurpose,
  analyzedPromptRunCount,
  analyzedPromptRunsLoading,
  granularity,
  grouping,
  onGroupingChange,
}: GovernanceAnalyticsSourcesProps) {
  const navigate = useNavigate();
  const ownColor = useMemo(() => readPrimaryColor(), []);
  const { index: authoritySources, ready: sourcesReady } = useAuthoritySources(api);
  // The aggregation credits citations to entities by KEY alone, so the names, the facets that
  // decide You from a rival, and the curation statuses are all resolved here.
  const entityTerms = useEntityTermIndex(api);

  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, retry] = useRetry();
  /** The raw sources block. undefined = this deployment does not serve it at all. */
  const [statsSources, setStatsSources] = useState<SourceStatsRow[] | undefined>(undefined);
  /** The unfiltered read, so narrowing to one brand cannot shrink the list of brands on offer. */
  const [unfilteredSources, setUnfilteredSources] = useState<SourceStatsRow[] | undefined>(undefined);
  /**
   * The denominator every row's percentage was measured against, from the same response as the rows.
   *
   * Deliberately not the `analyzedPromptRunCount` prop, though the two agree: that one comes from the
   * page's own call, and a figure a row states as ITS denominator has to be the figure that row's
   * share was divided by. Reading both off one payload is what makes them unable to disagree —
   * including while the page's KPI row is still settling. undefined = the deployment did not send
   * one, and the rows then state their numerator alone.
   */
  const [analyzedPromptRuns, setAnalyzedPromptRuns] = useState<number | undefined>(undefined);
  const [entityTermKey, setEntityTermKey] = useState<string>(ALL_ENTITIES);
  /** How much of the ranking is revealed. Reset whenever the window being ranked changes. */
  const [visibleCount, setVisibleCount] = useState(SOURCE_PAGE_SIZE);
  const [sortColumn, setSortColumn] = useState<SourceSortColumn>(DEFAULT_SOURCE_SORT);
  const [sortDirection, setSortDirection] = useState<GovernanceSortDirection>('desc');
  const [series, setSeries] = useState<Map<string, (number | undefined)[]>>(new Map());
  /**
   * Whether this deployment serves per-source trends at all.
   *
   * Starts true and is probed once against the answer itself, rather than against a version number:
   * a current Sync Core serves the per-source series and the column simply appears, while one older
   * than the release that added them answers without series and the column is dropped. A column of
   * dashes for every row would read as a broken feature rather than an absent one.
   */
  const [trendsAvailable, setTrendsAvailable] = useState(true);
  /** The series request failed: the rows stay, and the section offers to ask for their trends again. */
  const [trendsFailed, setTrendsFailed] = useState(false);
  const [trendsAttempt, retryTrends] = useRetry();

  const filtersSerialized = useMemo(() => JSON.stringify(statsFilters), [statsFilters]);
  const query = useMemo<SourceStatsQuery>(
    () => ({ filters: statsFilters, groupBy: grouping, entityTermKey: entityTermKey || undefined }),
    [statsFilters, grouping, entityTermKey]
  );

  /**
   * A new window starts at the first page.
   *
   * Keyed on everything that changes WHICH sources are ranked — the page filters, the grouping and
   * the entity narrowing — rather than on the rows themselves: a reader who reveals forty rows and
   * then narrows the filter is looking at a different ranking, and carrying their depth into it
   * would show forty rows of something they never scrolled.
   */
  useEffect(() => {
    setVisibleCount(SOURCE_PAGE_SIZE);
  }, [filtersSerialized, grouping, entityTermKey]);

  useEffect(() => {
    if (!api || !hasSelectedPurpose) {
      setLoading(false);
      setStatsSources(undefined);
      setUnfilteredSources(undefined);
      setAnalyzedPromptRuns(undefined);
      setSeries(new Map());
      setFailed(false);
      return;
    }

    let active = true;
    const superseded = new AbortController();
    setLoading(true);
    setFailed(false);

    void (async () => {
      try {
        const total = await fetchSourceStatsTotal(api, query, superseded.signal);
        if (!active) {
          return;
        }

        // Undefined means the deployment does not serve the sources block at all — not an error,
        // and not a zero. The rows are held raw and joined to the taxonomy below, so a name
        // resolving later repaints the table instead of costing another request.
        setStatsSources(total?.sources ?? undefined);
        // Only the unfiltered read may define the picker's choices; a filtered one has already
        // narrowed them, and a picker that can only narrow further is a trap.
        if (!entityTermKey) {
          setUnfilteredSources(total?.sources ?? undefined);
        }
        // Through the same reader the KPI card uses, so "one run is one answer" is decided in one
        // place rather than once per surface.
        setAnalyzedPromptRuns(total ? analyzedPromptRunCountOf(total) : undefined);
        if (!total) {
          setSeries(new Map());
        }
      } catch {
        if (active) {
          setStatsSources([]);
          setUnfilteredSources([]);
          setAnalyzedPromptRuns(undefined);
          setSeries(new Map());
          setFailed(true);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, hasSelectedPurpose, filtersSerialized, grouping, entityTermKey, query, attempt]);

  /**
   * The ranking, over the whole window.
   *
   * Ordered on the payload rather than on built rows so the sort covers every source the window
   * returned while only the revealed ones are ever built.
   */
  const rankedSources = useMemo(() => sortSourceStats(statsSources, sortColumn, sortDirection), [statsSources, sortColumn, sortDirection]);

  /**
   * The rows on screen, joined to both taxonomies.
   *
   * Deliberately a memo rather than part of the fetch: the source names and the entity names arrive
   * from caches that may settle after the figures do, and re-running the window query because a
   * name resolved would be a request for nothing.
   *
   * Built from the revealed slice alone, so resolving a source's curated name and crediting its
   * entities — the per-row work — is paid for the rows the reader has actually asked to see. The
   * figures that describe the WHOLE window are computed from the payload below instead, so revealing
   * more rows cannot move them.
   */
  const rows = useMemo(
    () => (statsSources ? buildSourceRows(rankedSources.slice(0, visibleCount), authoritySources, entityTerms.byTermKey) : undefined),
    [statsSources, rankedSources, visibleCount, authoritySources, entityTerms]
  );
  const entityOptions = useMemo(
    () => sourceEntityOptions(sourceStatsEntities(unfilteredSources, entityTerms.byTermKey)),
    [unfilteredSources, entityTerms]
  );

  // Trends for the rows on screen only — the series exist to draw those sparklines, and a row the
  // reader has not revealed has no sparkline to draw. Still capped, so revealing a long tail cannot
  // turn one click into an unbounded series request.
  const trendKeys = useMemo(() => (rows || []).slice(0, SOURCE_TREND_LIMIT).map((row) => row.key), [rows]);
  const trendKeysSerialized = useMemo(() => trendKeys.join(','), [trendKeys]);

  useEffect(() => {
    if (!api || !trendKeys.length || !trendsAvailable) {
      setSeries(new Map());
      setTrendsFailed(false);
      return;
    }

    let active = true;
    const superseded = new AbortController();
    setTrendsFailed(false);
    void (async () => {
      try {
        const buckets = await fetchSourceStatsHistory(api, query, granularity, TREND_BUCKETS, trendKeys, superseded.signal);
        if (!active) {
          return;
        }
        if (!buckets) {
          // Probed and answered without series: this deployment has no trends to show. Latching it
          // off drops the column instead of filling it with dashes, and stops re-asking.
          setTrendsAvailable(false);
          setSeries(new Map());
          return;
        }
        setSeries(new Map(trendKeys.map((key) => [key, buildSourceSeries(buckets, key, sortColumn)])));
      } catch {
        if (active) {
          // A failed request is not evidence that trends do not exist, so the column stays and the
          // rows report absence per row, while the section says so and offers to ask again.
          setSeries(new Map());
          setTrendsFailed(true);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, trendKeysSerialized, granularity, sortColumn, query, trendsAvailable, trendsAttempt]);

  /**
   * One colour assignment for the whole table, from the shared entity palette.
   *
   * Built from every entity in the window at once so a competitor keeps the same colour in every
   * row — a per-row assignment would give the same brand a different colour on each line.
   */
  const orderedColorEntities = useMemo(() => {
    const seen = new Map<string, { name: string; type: TaxonomyFacet; terms: string[]; citations: number }>();
    for (const entity of sourceStatsEntities(statsSources, entityTerms.byTermKey)) {
      // An entity that is neither the customer's nor a tracked rival takes NO colour — tinting it
      // would imply a relationship the project never declared — so it is left out of the
      // assignment entirely and its chip falls back to grey.
      if (!entity.name || entity.ownership === 'other') {
        continue;
      }
      const existing = seen.get(entity.termKey);
      if (existing) {
        existing.citations += entity.citations;
        continue;
      }
      seen.set(entity.termKey, {
        name: entity.name,
        // The palette branches on ownership alone, and ownership was already decided from the
        // term's real facet. Restating it as the canonical facet of that side keeps the two from
        // disagreeing without giving the palette a second thing to interpret.
        type: entity.ownership === 'own' ? TaxonomyFacet.Brand : TaxonomyFacet.Competitor,
        terms: [entity.termKey],
        citations: entity.citations,
      });
    }

    return Array.from(seen.values()).sort((left, right) => right.citations - left.citations);
  }, [statsSources, entityTerms]);

  // Stored colours ride the shared term index; their keys come from the entities, not the built map.
  const storedColorTermKeys = useMemo(() => collectColorableTermKeys(orderedColorEntities), [orderedColorEntities]);
  const storedTermColors = useTermColors(api, storedColorTermKeys);
  const entityColors = useMemo(
    () => buildEntityColorMap(orderedColorEntities, ownColor, GOVERNANCE_COMPARISON_PALETTE, undefined, storedTermColors),
    [orderedColorEntities, ownColor, storedTermColors]
  );

  const windowCitations = useMemo(() => totalCitations(statsSources), [statsSources]);

  const toggleSort = (column: SourceSortColumn) => {
    if (column === sortColumn) {
      setSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortColumn(column);
    setSortDirection('desc');
  };

  const drillDown = (row: SourceRow) => {
    const query = buildSourceDrillDownQuery(row, grouping, statsFilters);
    navigate(`/brand-presence/analytics/ai-answers?${new URLSearchParams(query).toString()}`);
  };

  const addAsSource = (row: SourceRow) => {
    // Through the existing term-creation flow rather than writing a term from an analytics screen:
    // the operator reviews and confirms what gets added to their taxonomy.
    navigate(createPath(TaxonomyFacet.AuthoritySource, { name: row.key, domain: row.key }));
  };

  // Hidden only when the project has no analyzed answers at all — and only once that number has
  // settled, so a card does not vanish and reappear while the KPI row is still loading.
  if (!hasSelectedPurpose || (!analyzedPromptRunsLoading && analyzedPromptRunCount === 0)) {
    return null;
  }

  return (
    <GovernanceAnalyticsSectionCard
      title="Sources"
      loading={loading}
      actions={
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">View sources for:</span>
            <select
              className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
              value={entityTermKey}
              onChange={(event) => setEntityTermKey(event.target.value)}
              disabled={!entityOptions.length}
            >
              <option value={ALL_ENTITIES}>All</option>
              {entityOptions.map((option) => (
                <option key={option.termKey} value={option.termKey}>
                  {option.ownership === 'own' ? `You — ${option.name}` : option.name}
                </option>
              ))}
            </select>
          </div>
          <SourceGroupingToggle grouping={grouping} onChange={onGroupingChange} />
        </div>
      }
    >
      <div className="text-xs text-slate-500 mb-3">{SOURCES_SECTION_CAPTION}</div>

      {failed ? (
        <SectionLoadFailed message={SOURCES_REQUEST_FAILED} onRetry={retry} />
      ) : loading && !rankedSources.length ? (
        // Sources that have not arrived are not sources that are not there.
        <TableRowsSkeleton />
      ) : !rankedSources.length ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">{SOURCES_EMPTY_STATE}</div>
      ) : (
        <div className="overflow-x-auto">
          {trendsFailed ? (
            <div className="mb-3">
              <SectionLoadFailed message={TRENDS_LOAD_FAILED_MESSAGE} onRetry={retryTrends} />
            </div>
          ) : null}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500 border-b border-slate-200/70">
                <th className="py-2 pe-3 font-semibold">Source</th>
                <th className="py-2 px-3 font-semibold w-56">
                  <span className="flex items-center gap-1">
                    <GovernanceSortableHeader
                      label="Cited in answers"
                      isActive={sortColumn === 'presence'}
                      direction={sortDirection}
                      onClick={() => toggleSort('presence')}
                    />
                    <GovernanceMetricInfo info={SOURCE_PRESENCE_DEFINITION} />
                  </span>
                </th>
                <th className="py-2 px-3 font-semibold w-40">
                  <span className="flex items-center gap-1">
                    <GovernanceSortableHeader
                      label="Share of citations"
                      isActive={sortColumn === 'citationShare'}
                      direction={sortDirection}
                      onClick={() => toggleSort('citationShare')}
                    />
                    <GovernanceMetricInfo info={SOURCE_CITATION_SHARE_DEFINITION} />
                  </span>
                </th>
                <th className="py-2 px-3 font-semibold">
                  <span className="flex items-center gap-1">
                    Supports
                    <GovernanceMetricInfo info={SOURCE_SUPPORTS_DEFINITION} />
                  </span>
                </th>
                {trendsAvailable ? <th className="py-2 ps-3 font-semibold w-24">Trend</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(rows || []).map((row) => (
                <SourceTableRow
                  key={row.key}
                  api={api}
                  row={row}
                  sourcesReady={sourcesReady}
                  analyzedPromptRuns={analyzedPromptRuns}
                  windowCitations={windowCitations}
                  colorByTermKey={entityColors.byTermKey}
                  storedTermKeys={entityColors.storedTermKeys}
                  showTrend={trendsAvailable}
                  series={series.get(row.key)}
                  ownColor={ownColor}
                  onDrillDown={() => drillDown(row)}
                  onAddAsSource={() => addAsSource(row)}
                />
              ))}
            </tbody>
          </table>

          {visibleCount < rankedSources.length ? (
            <div className="pt-3 flex items-center justify-center gap-3">
              <span className="text-xs text-slate-500">{sourceVisibilityLabel(rows?.length ?? 0, rankedSources.length)}</span>
              <button
                type="button"
                className="text-xs font-semibold text-sky-700 hover:underline"
                onClick={() => setVisibleCount((current) => current + SOURCE_PAGE_SIZE)}
              >
                {SOURCE_LOAD_MORE_LABEL}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </GovernanceAnalyticsSectionCard>
  );
}

function SourceTableRow({
  api,
  row,
  sourcesReady,
  analyzedPromptRuns,
  windowCitations,
  colorByTermKey,
  storedTermKeys,
  showTrend,
  series,
  ownColor,
  onDrillDown,
  onAddAsSource,
}: {
  api?: IFaviconUrlSource;
  row: SourceRow;
  sourcesReady: boolean;
  showTrend: boolean;
  /** The window's analyzed prompt runs — the denominator this row's percentage was measured against. */
  analyzedPromptRuns?: number;
  windowCitations?: number;
  colorByTermKey: Map<string, string>;
  /** Term keys whose colour is a stored brand colour — the ones darkened for a readable name. */
  storedTermKeys: Set<string>;
  series?: (number | undefined)[];
  ownColor: string;
  onDrillDown: () => void;
  onAddAsSource: () => void;
}) {
  const presence = formatPercent(row.presencePercent);
  const citationShare = formatPercent(row.citationSharePercent);
  /**
   * The chips this row can honestly draw.
   *
   * An entity the shared term cache could not resolve is dropped: the chip would either be blank or
   * carry an identifier, and neither tells the reader which brand a source backs. Archived terms go
   * the same way, through the shared status helper. Both still count toward the "+N", so the row
   * never claims to show more than it does.
   */
  const namedEntities = row.entities.filter((entity) => !!entity.name && !termStatusPresentation(entity.status, 'badge').hidden);
  const shownEntities = namedEntities.slice(0, SUPPORT_CHIP_LIMIT);
  const hiddenEntities = row.entities.length - shownEntities.length;

  return (
    <tr className="hover:bg-slate-50 cursor-pointer align-middle" onClick={onDrillDown} title="Show the answers citing this source">
      <td className="py-2 pe-3">
        <div className="flex items-center gap-2 min-w-0">
          <SourceFavicon api={api} domain={row.key} />
          {sourcesReady ? (
            <span
              className={['font-semibold text-slate-800 truncate', row.label.presentation.className].filter(Boolean).join(' ')}
              title={row.label.presentation.title ?? (row.label.isUncurated ? UNCURATED_SOURCE_HINT : row.label.label)}
            >
              {row.label.label}
            </span>
          ) : (
            // Held until the curated names land: a source must not read as its hostname and then
            // as its curated name within one page load.
            <span
              className="inline-block h-4 w-32 rounded bg-slate-100 animate-pulse"
              aria-label="Loading source name"
              role="presentation"
            />
          )}
          {row.label.presentation.showSuggestionIcon ? <SuggestedTermIcon spacing="flush" /> : null}
          {sourcesReady && row.label.isUncurated ? (
            <button
              type="button"
              className="text-[11px] text-sky-700 hover:underline flex-shrink-0"
              title={ADD_AS_SOURCE_HINT}
              onClick={(event) => {
                event.stopPropagation();
                onAddAsSource();
              }}
            >
              {ADD_AS_SOURCE_LABEL}
            </button>
          ) : null}
        </div>
      </td>

      <td className="py-2 px-3" title={presenceCountLabel(row, analyzedPromptRuns)}>
        {presence === undefined ? (
          // Never a zero-width bar: an unmeasured source has no share, and a bar of nothing reads
          // as a measured nothing.
          <span className="text-slate-300" title="Not measured for this window">
            —
          </span>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-800 tabular-nums w-12 text-right">{presence}</span>
            <div className="flex-grow-1 h-2 rounded bg-slate-100 overflow-hidden">
              <div
                className="h-full"
                style={{
                  // The percentage beside it, drawn. The track is the whole window, so a source
                  // cited in three answers in a hundred fills three percent of it. Scaling instead
                  // to the strongest source on screen gave the leading row a full bar whatever its
                  // share, which is how a 3% row came to read as total dominance.
                  //
                  // Bounded to the track here and nowhere else: an impossible percentage still
                  // PRINTS as itself beside a full bar, so the row reads as broken instead of
                  // reading as a plausible hundred percent.
                  width: `${barWidthPercent(row.presencePercent)}%`,
                  backgroundColor: ownColor,
                }}
              />
            </div>
          </div>
        )}
      </td>

      <td className="py-2 px-3 tabular-nums text-slate-600" title={citationCountLabel(row, windowCitations)}>
        {citationShare ?? <span className="text-slate-300">—</span>}
      </td>

      <td className="py-2 px-3">
        {shownEntities.length ? (
          <div className="flex items-center gap-2 flex-wrap">
            {shownEntities.map((entity) => {
              const color = colorByTermKey.get(entity.termKey);
              // Only a stored colour is darkened for the name; a palette colour reads as it does today.
              const nameColor = color && storedTermKeys.has(entity.termKey) ? readableTextColor(color) : (color ?? UNTRACKED_ENTITY_COLOR);
              const presentation = termStatusPresentation(entity.status, 'badge');
              return (
                <span
                  key={entity.termKey}
                  className={['flex items-center gap-1 text-[11px]', presentation.className].filter(Boolean).join(' ')}
                  title={presentation.title ?? `${entity.name} · ${entity.citations} ${entity.citations === 1 ? 'citation' : 'citations'}`}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color ?? UNTRACKED_ENTITY_COLOR }}
                    aria-hidden
                  />
                  <span className="max-w-[7rem] truncate" style={{ color: nameColor, fontWeight: 600 }}>
                    {entity.ownership === 'own' ? 'You' : entity.name}
                  </span>
                  {presentation.showSuggestionIcon ? <SuggestedTermIcon /> : null}
                </span>
              );
            })}
            {hiddenEntities > 0 ? <span className="text-[11px] text-slate-400">+{hiddenEntities}</span> : null}
          </div>
        ) : (
          <span className="text-[11px] text-slate-400" title="Cited without being tied to any brand you track.">
            Independent
          </span>
        )}
      </td>

      {showTrend ? (
        <td className="py-2 ps-3">
          <GovernanceSparkline values={series || []} color={ownColor} label={`Trend for ${row.label.label}`} />
        </td>
      ) : null}
    </tr>
  );
}
